import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardListIcon, CopyIcon, ExternalLinkIcon, LinkIcon, PencilIcon, PlusIcon, PowerIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createForm, deleteForm, duplicateForm, setFormStatus } from '../../services/formService';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
export function Forms() {
  const navigate = useNavigate();
  const {
    organizationId,
    forms,
    leads,
    landings
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    const form = await createForm(organizationId, newName.trim());
    setSaving(false);
    setCreating(false);
    setNewName('');
    navigate(`/app/formularios/${form.id}`);
  }
  return <>
      <PageHeader title="Formularios" description="Crea las preguntas que verán tus potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-777" data-mp="1rucxzs7b4q9h" />} onClick={() => setCreating(true)} data-id="element-776" data-mp="1rv4tz44tgq0a">
            Crear formulario
          </Button>} data-id="element-775" data-mp="1wmw98iiiv9b4" />

      {forms.length === 0 ? <EmptyState icon={<ClipboardListIcon className="h-5 w-5" data-id="element-779" data-mp="2flqpjvxmwhte" />} title="Todavía no tienes formularios" description="Crea tu primer formulario y asócialo a una landing para empezar a recibir leads." action={<Button onClick={() => setCreating(true)} data-id="element-780" data-mp="33hvf6lxw4bb0">Crear formulario</Button>} data-id="element-778" data-mp="2ketmme1me9ec" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-781" data-mp="2pr0dbtlt6ugx">
          {forms.map(form => {
        const responses = leads.filter(l => l.origin.formId === form.id).length;
        const linked = landings.filter(l => l.formId === form.id);
        return <li key={form.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-782" data-mp="2krnfyexba503">
                <div className="flex items-start justify-between gap-3" data-id="element-783" data-mp="26tfkycju5drk">
                  <div className="min-w-0" data-id="element-784" data-mp="2atgcmyvy3lgk">
                    <Link to={`/app/formularios/${form.id}`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-785" data-mp="4zzubfnqzywl">
                      {form.name}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground" data-id="element-786" data-mp="1thj9ykvo40xj">
                      {form.description || 'Sin descripción'}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-788" data-mp="1yhe0uofg0qil">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-789" data-mp="2m7rhzjwhp4ye" />,
              onSelect: () => navigate(`/app/formularios/${form.id}`)
            }, {
              label: 'Vista previa',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-790" data-mp="mv6yr31zve82" />,
              onSelect: () => window.open(`/formulario/${form.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-791" data-mp="t9umc7butny7" />,
              onSelect: () => navigator.clipboard?.writeText(`${window.location.origin}/formulario/${form.slug}`)
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-792" data-mp="ak667kcnwc2w" />,
              onSelect: () => duplicateForm(form.id)
            }, {
              label: form.status === 'activo' ? 'Desactivar' : 'Activar',
              icon: <PowerIcon className="h-4 w-4" data-id="element-793" data-mp="kckaokv4jwsr" />,
              onSelect: () => setFormStatus(form.id, form.status === 'activo' ? 'inactivo' : 'activo')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-794" data-mp="3bi01o3fjfioc" />,
              destructive: true,
              onSelect: () => setToDelete(form.id)
            }]} data-id="element-787" data-mp="23aqds1arf8vi" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-795" data-mp="2xhz2qtiu2lbi">
                  <div data-id="element-796" data-mp="2xja7avk3rx3o">
                    <dt className="text-xs text-muted-foreground" data-id="element-797" data-mp="2sq79gb8am1ho">Preguntas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-798" data-mp="2nrnlk5a52fmo">
                      {form.questions.length}
                    </dd>
                  </div>
                  <div data-id="element-799" data-mp="2iu48h8p6g8wb">
                    <dt className="text-xs text-muted-foreground" data-id="element-800" data-mp="2iqghag77fykr">Respuestas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-801" data-mp="2dsonfgn022dx">{responses}</dd>
                  </div>
                  <div data-id="element-802" data-mp="29so71qgcbh48">
                    <dt className="text-xs text-muted-foreground" data-id="element-803" data-mp="1z6r9dwsms6k3">Creado</dt>
                    <dd className="text-muted-foreground" data-id="element-804" data-mp="1z8vo79s2ipz5">
                      {formatDate(form.createdAt)}
                    </dd>
                  </div>
                  <div data-id="element-805" data-mp="1q74ja75cqn6c">
                    <dt className="text-xs text-muted-foreground" data-id="element-806" data-mp="1q7nqotxdlt64">Modificado</dt>
                    <dd className="text-muted-foreground" data-id="element-807" data-mp="1v67gj5f3bp3r">
                      {formatDate(form.updatedAt)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4" data-id="element-808" data-mp="2mwvg4d148mfb">
                  <Tag data-id="element-809" data-mp="295qkrn4s6gas">
                    {form.status === 'activo' ? 'Activo' : form.status === 'borrador' ? 'Borrador' : 'Inactivo'}
                  </Tag>
                  {linked.map(landing => <Tag key={landing.id} data-id="element-810" data-mp="36kkwlo3lr098">{landing.name}</Tag>)}
                  {linked.length === 0 && <span className="text-xs text-muted-foreground" data-id="element-811" data-mp="3fxldy4smbe8e">
                      Sin landing asociada
                    </span>}
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nuevo formulario" footer={<div className="flex justify-end gap-2" data-id="element-813" data-mp="2wzilth4rx16d">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-814" data-mp="2x23og8cwbl56">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!newName.trim()} data-id="element-815" data-mp="36yggz2wpfsyc">
              Crear
            </Button>
          </div>} data-id="element-812" data-mp="3kpuzl8mp9s4v">
        <Input id="nombre-form" label="Nombre del formulario" placeholder="Solicitud de presupuesto" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-816" data-mp="31xsi5v28i7tk" />
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar formulario" description="Las landings asociadas se quedarán sin formulario. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteForm(toDelete);
      setToDelete(null);
    }} data-id="element-817" data-mp="2bpcx2yrcqhs7" />
    </>;
}